import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import CreateNewDirectoryScheduleBoardModal from 'af-root/scenes/Company/Jobs/Preview/Attachments/CreateNewDirectoryScheduleBoardModal';
import WorkOrderCancelModal from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfo/CancelModal';
import WorkOrderDelayModal from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfo/DelayModal';
import PreviousFieldReportPreviewFRTModal from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfo/FieldReportTypePreviewModal';
import NotificationPreviewModal from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfo/NotificationPreviewModal';
import WorkOrderPauseModal from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfo/PauseModal';
import WorkOrderResumeModal from 'af-root/scenes/Company/ScheduleBoard/Shared/OrderInfo/ResumeModal';
import NotifyParticipantsModal from 'af-root/scenes/Company/WorkOrders/Table/NotifyParticipantsModal';
import WorkOrderCopyModal from 'af-root/scenes/Company/WorkOrders/Table/OrderCopyForm';

import type { W_Notification_FindExpectedNotifyResult_VM } from 'ab-api/web/notification/findExpectedNotifyResult';

import type { DailyTipViewModel } from 'ab-viewModels/dailyTip.viewModel';
import type FieldReportTypePreviewInModalVM from 'ab-viewModels/fieldReport/fieldReportTypeSidePanel.viewModel';
import type WorkOrderModalViewModel from 'ab-viewModels/scheduleBoardWorkOrderModal.viewModel';
import type { WorkOrderRevisionEmployee, WorkOrderRevisionTemporaryEmployee } from 'ab-viewModels/workOrderRevisionItem.viewModel';

import type WorkOrderCopyRM from 'ab-requestModels/workOrder/copyWorkOrders.requestModel';
import type { WorkOrderCancelForm, WorkOrderDelayForm, WorkOrderPauseForm } from 'ab-requestModels/workOrder.requestModel';

import type { ScheduleBoardWorkOrderResourceLookupsViewModel } from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardResourceLookup.viewModel';

import { uniqueArray } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import ConfirmationModal from 'af-components/ConfirmationModal';
import type { EditAttachmentProps } from 'af-components/DocumentsAttachmentsTree/TreeTableBody/EditAttachmentModal';
import EditAttachmentModal from 'af-components/DocumentsAttachmentsTree/TreeTableBody/EditAttachmentModal';
import type { DirectoryEntity } from 'af-components/DocumentsAttachmentsTree/types';

import * as AttachmentActions from 'af-actions/attachment';
import * as DailyTipActions from 'af-actions/dailyTip';
import * as NotifyActions from 'af-actions/notify';
import * as WorkOrderActions from 'af-actions/workOrder';

import ExpectedNotifyResultModal from '../ExpectedNotifyResultModal';
import { groupEmployeeWorkOrderAssignments, groupTemporaryEmployeeWorkOrderAssignments } from './helpers';

interface OwnProps {
	cancelOrder: (workOrderId: string, cancelForm: WorkOrderCancelForm) => Promise<void>;
	pauseOrder: (workOrderId: string, pauseForm: WorkOrderPauseForm) => Promise<void>;
	resumeOrder: (workOrderId: string) => Promise<void>;
	closeOrderInfoModal: () => void;
	fetchDirectoriesAfterChange: () => void;
	deleteOrder: (workOrderId: string) => void;
	notificationTemplate: string | undefined;
	publishOrder: (workOrderId: number, form?: WorkOrderDelayForm) => Promise<void>;
	workOrder: Nullable<WorkOrderModalViewModel>;
	/** Modal visibility setters */
	setCancelModalVisibility: (isVisible: boolean) => void;
	setPauseModalVisibility: (isVisible: boolean) => void;
	setResumeModalVisibility: (isVisible: boolean) => void;
	setCopyModalVisibility: (isVisible: boolean) => void;
	setDelayModalVisibility: (isVisible: boolean) => void;
	setDeleteModalVisibility: (isVisible: boolean) => void;
	setNotificationPreviewModalVisibility: (isVisible: boolean) => void;
	setNotifyParticipantsModalVisibility: (isVisible: boolean) => void;
	setWorkOrderModalVisibility: (isVisible: boolean) => void;
	setCancelWarningModalVisibility: (isVisible: boolean) => void;
	onCloseCreateNewFolderModal: () => void;
	onCloseDeleteDirectoryModal: () => void;
	onCloseRenameAttachmentModal: () => void;
	closeFieldReportTypePreviewModal: () => void;
	closeFieldReportTypeDeleteModal: () => void;
	onDeleteFieldReportType: () => void;
	/** Modal visibility flags */
	showCancelModal: boolean;
	showPauseModal: boolean;
	showResumeModal: boolean;
	showCopyModal: boolean;
	showDelayModal: boolean;
	showDeleteModal: boolean;
	showNotificationPreviewModal: boolean;
	showNotifyParticipantsModal: boolean;
	showCancelWarningModal: boolean;
	showNotifyResultModal: boolean;
	openNotifyResultModal: () => void;
	closeNotifyResultModal: () => void;
	showDeleteDirectoryModal: boolean;
	showNewDirectoryModal: boolean;
	showRenameAttachmentModal: boolean;
	fieldReportTypeToPreview: Nullable<FieldReportTypePreviewInModalVM>;
	fieldReportTypePreviewHeaderValue: Nullable<string>;
	showFieldReportTypeDeleteModal: boolean;
	newFolderModalParentDir: Nullable<DirectoryEntity>;
	deleteFolderModalDirId: Nullable<number>;
	renameAttachmentModalAttachment: Nullable<EditAttachmentProps>;
	removePreviousFRTypeName: Nullable<string>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const REMOVE_PREVIOUS_FRT_WARNING_BODY = (
	<>
		This will unlink this report type from the Work Order.
		<br />
		Are you sure you want to continue?
	</>
);

const REMOVE_PREVIOUS_FRT_WARNING_TITLE = (rtName: Nullable<string>) => {
	return `Remove ${rtName ? rtName : ''} report type link from the Work Order?`;
};

const DELETE_WARNING_BODY = (
	<>
		Are you sure you want to delete this Work Order?
		<br />
		This action cannot be undone.
	</>
);

const CANCEL_WARNING_BODY = (
	<>
		Work Order with Finalized WO Report can no longer be canceled
	</>
);

const resourceToResourceIdReducer = (resourceLookup: Nullable<ScheduleBoardWorkOrderResourceLookupsViewModel>) => {
	return Object.values(resourceLookup ?? {})?.reduce((_acc, _resource) => {
		if (_resource.workOrderEmployeeId && _resource.employeeId) {
			_acc.workOrderEmployees[_resource.workOrderEmployeeId] = _resource.employeeId;
		} else if (_resource.workOrderTemporaryEmployeeId && _resource.temporaryEmployeeId) {
			_acc.workOrderTemporaryEmployees[_resource.workOrderTemporaryEmployeeId] = _resource.temporaryEmployeeId;
		}
		return _acc;
	}, {
		workOrderEmployees: {},
		workOrderTemporaryEmployees: {},
	} as {
		workOrderEmployees: { [employeeId: number]: number; };
		workOrderTemporaryEmployees: { [temporaryEmployeeId: number]: number; };
	});
};

const OrderActionModals: React.FC<Props> = (props) => {
	const {
		isLoading,
		showDeleteModal,
		showDelayModal,
		showCancelModal,
		showNotifyParticipantsModal,
		showNotificationPreviewModal,
		showCopyModal,
		showCancelWarningModal,
		participantsEmailAndSmsStatus,
		temporaryParticipantsEmailAndSmsStatus,
		autoNotifyAt,
		enableScheduledNotifications,
		autoNotifyOnPublish,
		loadRevisions,
		notifyTemporaryLabor,
		closeOrderInfoModal,
		setDeleteModalVisibility,
		setCancelModalVisibility,
		setPauseModalVisibility,
		setResumeModalVisibility,
		setWorkOrderModalVisibility,
		setDelayModalVisibility,
		setCopyModalVisibility,
		setNotifyParticipantsModalVisibility,
		setNotificationPreviewModalVisibility,
		setCancelWarningModalVisibility,
		scheduleWorkOrderParticipants,
		notifyWorkOrderParticipants,
		closeFieldReportTypePreviewModal,
		employees,
		temporaryEmployees,
		publishOrder,
		copyWorkOrder,
		cancelOrder,
		pauseOrder,
		deleteOrder,
		resumeOrder,
		createCustomDailyTip,
		resourceLookup,
		dailyTip = {} as DailyTipViewModel,
		initialCopyModalData,
		workOrder,
		notificationTemplate,
		showPauseModal,
		showResumeModal,
		findExpectedResultsForNotify,
		closeNotifyResultModal,
		openNotifyResultModal,
		showNotifyResultModal,
		createDirectory,
		fetchDirectoriesAfterChange,
		onCloseCreateNewFolderModal,
		onCloseDeleteDirectoryModal,
		onCloseRenameAttachmentModal,
		renameAttachment,
		deleteDirectory,
		showDeleteDirectoryModal,
		showNewDirectoryModal,
		showRenameAttachmentModal,
		fieldReportTypeToPreview,
		fieldReportTypePreviewHeaderValue,
		showFieldReportTypeDeleteModal,
		closeFieldReportTypeDeleteModal,
		onDeleteFieldReportType,
		newFolderModalParentDir,
		deleteFolderModalDirId,
		renameAttachmentModalAttachment,
		removePreviousFRTypeName,
	} = props;

	const [workOrderEmployees, setWorkOrderEmployees] = React.useState<{ [workOrderEmployeeId: number]: number; }>({});
	const [workOrderTemporaryEmployees, setWorkOrderTemporaryEmployees] = React.useState<{ [workOrderTemporaryEmployeeId: number]: number; }>({});
	const [revisionEmployees, setRevisionEmployees] = React.useState<{ [revisionId: number]: WorkOrderRevisionEmployee[]; }>({});
	const [revisionTemporaryEmployees, setRevisionTemporaryEmployees] = React.useState<{ [revisionId: number]: WorkOrderRevisionTemporaryEmployee[]; }>({});
	const [
		assignmentsThatMightNotBeNotified,
		setAssignmentsThatMightNotBeNotified,
	] = React.useState<Nullable<W_Notification_FindExpectedNotifyResult_VM>>(null);

	const loadRevisionEmployees = React.useCallback(async () => {
		if (!workOrder) {
			throw new Error('Work order not found');
		}
		const revisions = await loadRevisions(workOrder.id);
		const newRevisionEmployees = {} as typeof revisionEmployees;
		const newRevisionTemporaryEmployees = {} as typeof revisionTemporaryEmployees;

		revisions.forEach((revision) => {
			// using instanceof causes runtime error "Can't resolve 'ab-models/workOrder.model' in node/src/dataTransferObjects/viewModels'
			if ('employees' in revision && revision.employees.length > 0 && revision.id) {
				newRevisionEmployees[revision.id] = revision.employees;
			}
			if ('temporaryEmployees' in revision && revision.temporaryEmployees.length > 0 && revision.id) {
				newRevisionTemporaryEmployees[revision.id] = revision.temporaryEmployees;
			}
		});
		setRevisionEmployees(newRevisionEmployees);
		setRevisionTemporaryEmployees(newRevisionTemporaryEmployees);
	}, [loadRevisions, workOrder]);

	React.useEffect(() => {
		loadRevisionEmployees();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		const {
			workOrderEmployees: _workOrderEmployees,
			workOrderTemporaryEmployees: _workOrderTemporaryEmployees,
		} = resourceToResourceIdReducer(resourceLookup);
		setWorkOrderEmployees(_workOrderEmployees);
		setWorkOrderTemporaryEmployees(_workOrderTemporaryEmployees);
	}, [resourceLookup]);

	React.useEffect(() => {
		loadRevisionEmployees();
	}, [workOrder?.id, workOrder?.revision, loadRevisionEmployees]);

	const closeDeleteModal = React.useCallback(() => {
		closeOrderInfoModal();
		setDeleteModalVisibility(false);
	}, [closeOrderInfoModal, setDeleteModalVisibility]);

	const closeCancelModal = React.useCallback(() => {
		closeOrderInfoModal();
		setCancelModalVisibility(false);
	}, [closeOrderInfoModal, setCancelModalVisibility]);

	const closePauseModal = React.useCallback(() => {
		closeOrderInfoModal();
		setPauseModalVisibility(false);
	}, [closeOrderInfoModal, setPauseModalVisibility]);

	const closeResumeModal = React.useCallback(() => {
		closeOrderInfoModal();
		setResumeModalVisibility(false);
	}, [closeOrderInfoModal, setResumeModalVisibility]);

	const closeDelayModal = React.useCallback(() => {
		setWorkOrderModalVisibility(true);
		setDelayModalVisibility(false);
	}, [setWorkOrderModalVisibility, setDelayModalVisibility]);

	const closeCopyModal = React.useCallback(() => {
		closeOrderInfoModal();
		setCopyModalVisibility(false);
	}, [closeOrderInfoModal, setCopyModalVisibility]);

	const closeNotifyParticipantsModal = React.useCallback(() => {
		setWorkOrderModalVisibility(true);
		setNotifyParticipantsModalVisibility(false);
	}, [setNotifyParticipantsModalVisibility, setWorkOrderModalVisibility]);

	const closeNotificationPreviewModal = React.useCallback(() => {
		setWorkOrderModalVisibility(true);
		setNotificationPreviewModalVisibility(false);
	}, [setWorkOrderModalVisibility, setNotificationPreviewModalVisibility]);

	const closeExpectedNotifyResultModal = React.useCallback(() => {
		setWorkOrderModalVisibility(true);
		closeNotifyResultModal();
		setAssignmentsThatMightNotBeNotified(null);
	}, [closeNotifyResultModal, setWorkOrderModalVisibility]);

	const closeCancelWarningModal = React.useCallback(() => {
		setCancelWarningModalVisibility(false);
		setWorkOrderModalVisibility(true);
	}, [setCancelWarningModalVisibility, setWorkOrderModalVisibility]);

	const closeFieldReportTypeModal = React.useCallback(() => {
		setWorkOrderModalVisibility(true);
		closeFieldReportTypePreviewModal();
	}, [closeFieldReportTypePreviewModal, setWorkOrderModalVisibility]);

	const onCancelOrder = React.useCallback(async (cancelForm: WorkOrderCancelForm) => {
		if (!workOrder) {
			throw new Error('Work order not found');
		}
		await cancelOrder(`${workOrder.id}`, cancelForm);

		if (showCancelModal) {
			closeCancelModal();
		}
	}, [cancelOrder, closeCancelModal, showCancelModal, workOrder]);

	const onPauseOrder = React.useCallback(async (form: WorkOrderPauseForm) => {
		if (!workOrder) {
			throw new Error('Work order not found');
		}
		await pauseOrder(`${workOrder.id}`, form);

		if (showPauseModal) {
			closePauseModal();
		}
	}, [workOrder, pauseOrder, showPauseModal, closePauseModal]);

	const onResumeOrder = React.useCallback(async () => {
		if (!workOrder) {
			throw new Error('Work order not found');
		}
		await resumeOrder(`${workOrder.id}`);

		if (showResumeModal) {
			closeResumeModal();
		}
	}, [workOrder, resumeOrder, showResumeModal, closeResumeModal]);

	const onDeleteOrder = React.useCallback(() => {
		if (!workOrder) {
			throw new Error('Work order not found');
		}
		deleteOrder(`${workOrder.id}`);
		closeDeleteModal();
	}, [workOrder, deleteOrder, closeDeleteModal]);

	const onDeletePreviousFieldReportType = React.useCallback(() => {
		onDeleteFieldReportType();
	}, [onDeleteFieldReportType]);

	const onCreateCustomDailyTip = React.useCallback(async (message: string) => {
		if (!workOrder) {
			throw new Error('Work Order not defined');
		}
		const { dueDate } = workOrder;

		const customTip = await createCustomDailyTip(message, dueDate, true);
		return customTip.id;
	}, [workOrder, createCustomDailyTip]);

	const handleShowExpectedNotifyResult = React.useCallback(async (
		event: {
			employeeIdsForSMS: number[];
			employeeIdsForEmail: number[];
			temporaryEmployeeIdsForSMS: number[];
			temporaryEmployeeIdsForEmail: number[];
			workOrderId: number;
		}
	) => {

		const {
			employeeIdsForEmail,
			employeeIdsForSMS,
			workOrderId,
			temporaryEmployeeIdsForEmail,
			temporaryEmployeeIdsForSMS,
		} = event;

		const uniqueEmployeeIds = uniqueArray([...employeeIdsForEmail, ...employeeIdsForSMS]);
		const uniqueTemporaryEmployeeIds = uniqueArray([...temporaryEmployeeIdsForEmail, ...temporaryEmployeeIdsForSMS]);
		const expectedNotifyResults = await findExpectedResultsForNotify(
			{
				employeeAssignments: [{ workOrderId, employeeIds: uniqueEmployeeIds }],
				temporaryEmployeeAssignments: [{ workOrderId, temporaryEmployeeIds: uniqueTemporaryEmployeeIds }],
			}
		);

		if (expectedNotifyResults.employeeAssignmentsThatWontReceiveNotification.flatMap((_en) => [..._en.employeeFullNames]).length
			|| expectedNotifyResults.temporaryEmployeeAssignmentsThatWontReceiveNotification.flatMap((_ten) => [..._ten.temporaryEmployeeFullNames]).length
		) {
			setAssignmentsThatMightNotBeNotified(expectedNotifyResults);
			openNotifyResultModal();
		} else { // Nothing to show
			setWorkOrderModalVisibility(true);
		}
	}, [findExpectedResultsForNotify, openNotifyResultModal, setWorkOrderModalVisibility]);

	const notifyParticipants = React.useCallback(async (
		employeeIdsForSms: number[],
		employeeIdsForEmail: number[],
		temporaryEmployeeIdsForSms: number[],
		temporaryEmployeeIdsForEmail: number[],
		workOrderId: number,
		message?: string,
		isEdited?: boolean
	) => {
		if (!workOrder) {
			throw new Error('Work Order not defined');
		}

		let customTipId = 0;
		if (message) {
			customTipId = isEdited ? await onCreateCustomDailyTip(message) : dailyTip!.id;
		}

		await notifyWorkOrderParticipants({
			employeeEmailIds: employeeIdsForEmail,
			employeeSmsIds: employeeIdsForSms,
			tempEmployeeSmsIds: temporaryEmployeeIdsForSms,
			tempEmployeeEmailIds: temporaryEmployeeIdsForEmail,
			dailyTipId: !!customTipId ? customTipId : null,
			workOrderId,
		});

		setNotifyParticipantsModalVisibility(false);
		await handleShowExpectedNotifyResult({
			employeeIdsForEmail: employeeIdsForEmail,
			employeeIdsForSMS: employeeIdsForSms,
			workOrderId,
			temporaryEmployeeIdsForEmail: temporaryEmployeeIdsForEmail,
			temporaryEmployeeIdsForSMS: temporaryEmployeeIdsForSms,
		});
	}, [workOrder, handleShowExpectedNotifyResult, notifyWorkOrderParticipants, setNotifyParticipantsModalVisibility, onCreateCustomDailyTip, dailyTip]);

	const onCopyOrder = React.useCallback(async (copyForm: WorkOrderCopyRM) => {
		await copyWorkOrder(copyForm);
		closeCopyModal();
	}, [copyWorkOrder, closeCopyModal]);

	const onPublish = React.useCallback((publishForm: WorkOrderDelayForm) => {
		if (!workOrder) {
			throw new Error('Work order not found');
		}
		return publishOrder(workOrder?.id, publishForm);
	}, [workOrder, publishOrder]);

	const showExpectedNotifyModalForSchedule = React.useCallback(async (
		event: {
			workOrderId: number;
			workOrderEmployeeIdsForSms: number[];
			workOrderEmployeeIdsForEmail: number[];
			workOrderTemporaryEmployeeIdsForSms: number[];
			workOrderEmployeeTemporaryIdsForEmail: number[];
			/** Map of employee ids -> work order ids removed from WO */
			removedEmployeeSMSIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedEmployeeEmailIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedTemporaryEmployeeSMSIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedTemporaryEmployeeEmailIdsMap?: { [employeeId: number]: number; };
		}
	) => {

		const {
			workOrderId,
			workOrderEmployeeIdsForEmail,
			workOrderEmployeeIdsForSms,
			workOrderEmployeeTemporaryIdsForEmail,
			workOrderTemporaryEmployeeIdsForSms,
			removedEmployeeEmailIdsMap,
			removedEmployeeSMSIdsMap,
			removedTemporaryEmployeeEmailIdsMap,
			removedTemporaryEmployeeSMSIdsMap,
		} = event;

		const employeeIdsForEmail = groupEmployeeWorkOrderAssignments(
			{
				workOrderEmployeeIds: workOrderEmployeeIdsForEmail,
				removedEmployeeIdToWorkOrderIdLookup: removedEmployeeEmailIdsMap ?? {},
				workOrderEmployeeIdToEmployeeIdLookup: workOrderEmployees,
			});
		const employeeIdsForSMS = groupEmployeeWorkOrderAssignments(
			{
				workOrderEmployeeIds: workOrderEmployeeIdsForSms,
				removedEmployeeIdToWorkOrderIdLookup: removedEmployeeSMSIdsMap ?? {},
				workOrderEmployeeIdToEmployeeIdLookup: workOrderEmployees,
			});
		const temporaryEmployeeIdsForEmail = groupTemporaryEmployeeWorkOrderAssignments(
			{
				workOrderTemporaryEmployeeIds: workOrderEmployeeTemporaryIdsForEmail,
				removedTemporaryEmployeeIdToWorkOrderIdLookup: removedTemporaryEmployeeEmailIdsMap ?? {},
				workOrderTemporaryEmployeeIdToEmployeeIdLookup: workOrderTemporaryEmployees,
			});
		const temporaryEmployeeIdsForSMS = groupTemporaryEmployeeWorkOrderAssignments(
			{
				workOrderTemporaryEmployeeIds: workOrderTemporaryEmployeeIdsForSms,
				removedTemporaryEmployeeIdToWorkOrderIdLookup: removedTemporaryEmployeeSMSIdsMap ?? {},
				workOrderTemporaryEmployeeIdToEmployeeIdLookup: workOrderEmployees,
			});

		await handleShowExpectedNotifyResult({
			employeeIdsForEmail,
			employeeIdsForSMS,
			workOrderId,
			temporaryEmployeeIdsForEmail,
			temporaryEmployeeIdsForSMS,
		});

	}, [handleShowExpectedNotifyResult, workOrderEmployees, workOrderTemporaryEmployees]);

	const scheduleAutoNotify = React.useCallback(async (
		event: {
			workOrderId: number;
			workOrderEmployeeIdsForSms: number[];
			workOrderEmployeeIdsForEmail: number[];
			workOrderTemporaryEmployeeIdsForSms: number[];
			workOrderEmployeeTemporaryIdsForEmail: number[];
			message?: string;
			/** Map of employee ids -> work order ids removed from WO */
			removedEmployeeSMSIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedEmployeeEmailIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedTemporaryEmployeeSMSIdsMap?: { [employeeId: number]: number; };
			/** Map of employee ids -> work order ids removed from WO */
			removedTemporaryEmployeeEmailIdsMap?: { [employeeId: number]: number; };
		}
	) => {
		const {
			workOrderEmployeeIdsForEmail,
			workOrderEmployeeIdsForSms,
			workOrderEmployeeTemporaryIdsForEmail,
			workOrderTemporaryEmployeeIdsForSms,
			message,
			removedEmployeeEmailIdsMap,
			removedEmployeeSMSIdsMap,
			removedTemporaryEmployeeEmailIdsMap,
			removedTemporaryEmployeeSMSIdsMap,
		} = event;

		if (!workOrder) {
			throw new Error('Work order not found');
		}
		const { dueDate } = workOrder;

		const customTipId = message ? await onCreateCustomDailyTip(message) : undefined;

		await scheduleWorkOrderParticipants({
			dueDate: TimeUtils.formatDate(dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			workOrderEmployeeEmailIds: workOrderEmployeeIdsForEmail,
			workOrderEmployeeSmsIds: workOrderEmployeeIdsForSms,
			tempEmployeeEmailIds: workOrderEmployeeTemporaryIdsForEmail,
			tempEmployeeSmsIds: workOrderTemporaryEmployeeIdsForSms,
			dailyTipId: customTipId,
			removedEmployeeSMSIdsMap,
			removedEmployeeEmailIdsMap,
			removedTemporaryEmployeeEmailIdsMap,
			removedTemporaryEmployeeSMSIdsMap,
		});

		setNotifyParticipantsModalVisibility(false);
		await showExpectedNotifyModalForSchedule(event);
	}, [onCreateCustomDailyTip, scheduleWorkOrderParticipants, setNotifyParticipantsModalVisibility, showExpectedNotifyModalForSchedule, workOrder]);

	const workOrderStatus = workOrder?.status;
	const isPublished: boolean = workOrderStatus === WorkOrderStatus.PUBLISHED;
	const isCanceled: boolean = workOrderStatus === WorkOrderStatus.CANCELED;
	const isOutdated: boolean = workOrderStatus === WorkOrderStatus.OUTDATED;
	const isPaused: boolean = workOrder?.isPaused ?? false;

	const createNewDirectory = React.useCallback(async (parent: DirectoryEntity, name: string) => {
		if (workOrder) {
			await createDirectory(workOrder.id, parent.id, name);
			fetchDirectoriesAfterChange();
		}
	}, [createDirectory, workOrder, fetchDirectoriesAfterChange]);

	const deleteFolder = React.useCallback(async () => {
		if (workOrder && deleteFolderModalDirId) {
			await deleteDirectory(workOrder.id, deleteFolderModalDirId);
			fetchDirectoriesAfterChange();
		}
	}, [workOrder, deleteFolderModalDirId, deleteDirectory, fetchDirectoriesAfterChange]);

	const updateAttachmentName = React.useCallback(async (_directoryId: number, attachmentId: number, name: string) => {
		await renameAttachment(_directoryId, attachmentId, name);
		fetchDirectoriesAfterChange();
	}, [renameAttachment, fetchDirectoriesAfterChange]);

	return workOrder ? (
		<>
			<WorkOrderCancelModal
				closeCancelModal={closeCancelModal}
				onSubmit={onCancelOrder}
				showCancelModal={showCancelModal}
				workOrderCode={workOrder?.code}
			/>
			<WorkOrderPauseModal
				closePauseModal={closePauseModal}
				onSubmit={onPauseOrder}
				showPauseModal={showPauseModal}
				workOrderCode={workOrder?.code}
			/>
			<WorkOrderResumeModal
				closeResumeModal={closeResumeModal}
				resumeWorkOrder={onResumeOrder}
				showResumeModal={showResumeModal}
				workOrderCode={workOrder?.code}
			/>
			<WorkOrderDelayModal
				closeDelayModal={closeDelayModal}
				onSubmit={onPublish}
				showDelayModal={showDelayModal}
				workOrderCode={workOrder?.code}
			/>
			{showNotifyParticipantsModal && participantsEmailAndSmsStatus && (!!employees || !!temporaryEmployees) && notificationTemplate &&
				<NotifyParticipantsModal
					autoNotifyAt={autoNotifyAt}
					autoNotifyOnPublish={autoNotifyOnPublish}
					closeModal={closeNotifyParticipantsModal}
					dailyTip={dailyTip?.message ?? ''}
					enableScheduledNotifications={enableScheduledNotifications}
					isLoading={isLoading}
					notificationTemplate={notificationTemplate}
					notifyParticipants={notifyParticipants}
					notifyTemporaryLabor={notifyTemporaryLabor}
					order={workOrder}
					participantsEmailAndSmsStatus={participantsEmailAndSmsStatus}
					revisionEmployees={revisionEmployees}
					revisionTemporaryEmployees={revisionTemporaryEmployees}
					scheduleAutoNotify={scheduleAutoNotify}
					scheduleBoardEmployees={employees}
					scheduleBoardTemporaryEmployees={temporaryEmployees}
					showModal={showNotifyParticipantsModal}
					temporaryParticipantsEmailAndSmsStatus={temporaryParticipantsEmailAndSmsStatus}
					workOrderEmployees={workOrderEmployees}
					workOrderTemporaryEmployees={workOrderTemporaryEmployees}
				/>
			}
			{assignmentsThatMightNotBeNotified &&
				<ExpectedNotifyResultModal
					closeModal={closeExpectedNotifyResultModal}
					expectedNotifyResult={assignmentsThatMightNotBeNotified}
					showModal={showNotifyResultModal && !showNotifyParticipantsModal}
				/>
			}
			{(isPublished || isCanceled || isOutdated || isPaused) && !!notificationTemplate &&
				<NotificationPreviewModal
					closeModal={closeNotificationPreviewModal}
					isLoading={isLoading}
					notificationTemplate={notificationTemplate}
					showModal={showNotificationPreviewModal}
				/>
			}
			<WorkOrderCopyModal
				closeCopyModal={closeCopyModal}
				initialFormData={initialCopyModalData}
				onSubmit={onCopyOrder}
				showCopyModal={showCopyModal}
			/>
			<ConfirmationModal
				body={DELETE_WARNING_BODY}
				closeModal={closeDeleteModal}
				confirmAction={onDeleteOrder}
				confirmText="Delete Work Order"
				modalStyle="danger"
				showModal={showDeleteModal}
				size="md"
				title={`Delete Work Order (${workOrder?.code ?? ''})?`}
			/>
			<ConfirmationModal
				body={REMOVE_PREVIOUS_FRT_WARNING_BODY}
				closeModal={closeFieldReportTypeDeleteModal}
				confirmAction={onDeletePreviousFieldReportType}
				confirmText="Remove"
				modalStyle="danger"
				showModal={showFieldReportTypeDeleteModal}
				size="md"
				title={REMOVE_PREVIOUS_FRT_WARNING_TITLE(removePreviousFRTypeName)}
			/>
			<ConfirmationModal
				body={CANCEL_WARNING_BODY}
				closeModal={closeCancelWarningModal}
				confirmAction={closeCancelWarningModal}
				confirmText="OK"
				modalStyle="warning"
				showCancel={false}
				showModal={showCancelWarningModal}
				size="sm"
				title="Can't Cancel Work Order"
			/>
			<CreateNewDirectoryScheduleBoardModal
				closeModal={onCloseCreateNewFolderModal}
				createDirectory={createNewDirectory}
				parent={newFolderModalParentDir}
				showModal={showNewDirectoryModal}
			/>
			<ConfirmationModal
				body="You are about to permanently delete the Directory and all the files in it. Are you sure you want to continue?"
				closeModal={onCloseDeleteDirectoryModal}
				confirmAction={deleteFolder}
				confirmText="Delete"
				modalStyle="danger"
				showModal={showDeleteDirectoryModal}
				title={'Delete Directory'}
			/>
			{
				renameAttachmentModalAttachment &&
				<EditAttachmentModal
					attachmentData={renameAttachmentModalAttachment}
					setModalVisible={onCloseRenameAttachmentModal}
					showModal={showRenameAttachmentModal}
					updateAttachment={updateAttachmentName}
				/>
			}
			{fieldReportTypeToPreview && fieldReportTypePreviewHeaderValue &&
				<PreviousFieldReportPreviewFRTModal
					fieldReportTypeToPreview={fieldReportTypeToPreview}
					headerValue={fieldReportTypePreviewHeaderValue}
					onClose={closeFieldReportTypeModal}
				/>
			}
		</>
	) : null;
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { workOrder } = ownProps;
	const { workOrdersByDateDictionary, employees, temporaryEmployees } = state.scheduleBoard;
	const { company } = state.company;

	const dueDate = workOrder?.dueDate;
	const stateOnDate = dueDate ? workOrdersByDateDictionary?.[dueDate] : undefined;

	const selectedDayOfWeek = dueDate && TimeUtils.parseMoment(dueDate, TimeFormat.DATE_ONLY)?.day();
	const dayBefore = selectedDayOfWeek ? selectedDayOfWeek - 1 : 6;

	const isWorkOrderCanceled = workOrder?.status === WorkOrderStatus.CANCELED;
	const employeeStatuses = isWorkOrderCanceled
		? stateOnDate?.assignedCanceledNotificationStatuses
		: stateOnDate?.assignedPublishedNotificationStatuses;
	const temporaryEmployeeStatuses = isWorkOrderCanceled
		? stateOnDate?.assignedCanceledTempLaborNotificationStatuses
		: stateOnDate?.assignedPublishedTempLaborNotificationStatuses;

	const resourceLookup = isWorkOrderCanceled
		? stateOnDate?.canceledWorkOrderResourceLookups
		: stateOnDate?.workOrderResourceLookups;

	return {
		participantsEmailAndSmsStatus: employeeStatuses ?? null,
		temporaryParticipantsEmailAndSmsStatus: temporaryEmployeeStatuses ?? null,
		isLoading: state.http.isFetching,
		autoNotifyOnPublish: company?.notification?.notifyOnPublish ?? null,
		enableScheduledNotifications: company?.notification?.enableScheduledNotifications ?? false,
		notifyTemporaryLabor: company?.notification.notifyTemporaryLabor ?? false,
		autoNotifyAt: dueDate
			? company?.notification?.notifyOnDay?.[dayBefore] ?? null
			: null,
		employees,
		temporaryEmployees,
		initialCopyModalData: state.workOrder.copyWorkOrderModalData,
		dailyTip: stateOnDate?.dailyTip,
		resourceLookup: resourceLookup ?? null,
	};
}

function mapDispatchToProps() {
	return {
		copyWorkOrder: WorkOrderActions.copyWorkOrder,
		createDirectory: WorkOrderActions.createDirectory,
		deleteDirectory: WorkOrderActions.deleteDirectory,
		renameAttachment: AttachmentActions.renameAttachment,
		createCustomDailyTip: DailyTipActions.create,
		notifyWorkOrderParticipants: NotifyActions.sendTemplateNotificationSingleWO,
		findExpectedResultsForNotify: NotifyActions.findExpectedResultsForNotify,
		scheduleWorkOrderParticipants: NotifyActions.scheduleForAutoNotify,
		loadRevisions: WorkOrderActions.lazyLoadRevisions,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(OrderActionModals);
